'use client';

import Image from 'next/image';
import Container from '@/components/ui/container';
import { parseHeading } from '@/lib/utils';

const DEFAULT_PARTNERS = [
	{ name: 'Daikin', logo: '/images/daikin-logo.png' }, // Placeholder paths, usually exist or need upload
	{ name: 'Mitsubishi', logo: '/images/mitsubishi-logo.png' },
	{ name: 'Samsung', logo: '/images/samsung-logo.png' },
	{ name: 'LG', logo: '/images/lg-logo.png' },
];

interface PartnersProps {
	data: {
		title?: string;
		partners?: {
			name: string;
			logo: any; // Directus file or string
			url?: string;
		}[];
	};
}

export default function Partners({ data }: PartnersProps) {
	const title = data?.title || 'Onze Partners';
	const partners = Array.isArray(data?.partners) && data.partners.length > 0 ? data.partners : [];

	// If no partners and no default logos exist (mock mode usually implies we might not have files yet),
	// we can either hide or show placeholders. For now, let's render a simple text fallback if empty.

	if (partners.length === 0) {
		// Fallback or empty return.
		// Returning null as requested "graceful fallback" -> if no content, don't break, maybe show nothing.
		// But user asked for hydration readiness. Let's return a placeholder section only if explicitly needed?
		// Actually, let's keep it simple: if no data, don't render.
		// BUT, for the "mock" state, we might want to see *something*.
		// Let's render a structural placeholder.
		return null;
	}

	return (
		<section className="py-12 bg-white">
			<Container>
				<div className="text-center mb-8">
					<p className="text-sm uppercase tracking-wider text-gray-muted">{parseHeading(title)}</p>
				</div>
				<div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
					{partners.map((partner, index) => {
						const logoSrc =
							typeof partner.logo === 'string'
								? partner.logo
								: partner.logo?.filename_disk
									? `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${partner.logo.filename_disk}`
									: '';

						if (!logoSrc)
							return (
								<span key={index} className="font-bold text-xl">
									{partner.name}
								</span>
							);

						return (
							<div key={index} className="relative w-32 h-16">
								<Image src={logoSrc} alt={partner.name} fill className="object-contain" />
							</div>
						);
					})}
				</div>
			</Container>
		</section>
	);
}
